import { Injectable, Injector, inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import { PaymentService } from '../../payment/data-access/payment.service';
import { ModalService } from './modal.service';

/**
 * TimerService is a service that provides functionality to handle a countdown timer,
 * specifically for payment processes. It uses RxJS's BehaviorSubject and Subscription
 * to manage and emit countdown values. The timer can be used to enforce time limits
 * on certain actions, such as completing a payment. It integrates with a modal service
 * to provide user feedback when the timer ends and with a payment service to handle
 * payment cancellation. The service allows starting, setting, resetting, and unsubscribing
 * the timer as well as handling checkout cancellation.
 */
@Injectable({
  providedIn: 'root',
})
export class TimerService {
  private _injector = inject(Injector);
  private _router = inject(Router);
  private _modalService = inject(ModalService);

  private _countdown = new BehaviorSubject<number | null>(null);
  public countdown$ = this._countdown.asObservable();

  private ref: number | undefined = 0;

  /**
   * Flag to indicate if the checkout process has been canceled.
   */
  public isCheckoutCanceled = false;

  private routeToNavigateBack = '/seat-selection';

  /**
   * Starts the timer with a given duration in milliseconds.
   * @param miliseconds The duration for the countdown timer.
   */
  public startTimer = (miliseconds: number) => {
    clearInterval(this.ref);
    const expiryDate = Date.now() + miliseconds;
    const initialDiff = expiryDate - Date.now();
    this.setTimer(initialDiff);

    this.ref = setInterval(() => {
      const diff = expiryDate - Date.now();
      this.setTimer(diff);

      if (diff <= 0) {
        this.handleTimerCompletion();
      }
    }, 1000);
  };

  /**
   * Sets the timer to a specific value.
   * @param countdown The specific countdown time to set.
   */
  public setTimer = (countdown: number) => {
    this._countdown.next(countdown);
  };

  /**
   * Handler for checkout cancellation. It cancels the payment request, closes the modal,
   * navigates to the predefined route, resets the timer, and sets the checkout cancellation flag.
   */
  public onCheckoutCanceled = () => {
    const paymentService = this._injector.get(PaymentService);
    paymentService.cancelHttpPaymentRequest();
    this._modalService.closeModal();
    this._router.navigate([this.routeToNavigateBack]);
    this.resetTimer();
    paymentService.resetTransaction();
    this.isCheckoutCanceled = true;
  };

  /**
   * Handles the completion of the timer. It stops the timer, cancels the payment,
   * and opens a modal with defined actions.
   */
  private handleTimerCompletion = () => {
    this.resetTimer();
    const paymentService = this._injector.get(PaymentService);
    paymentService.cancelHttpPaymentRequest();
    const modalData = {
      type: 'app',
      text: 'Checkout validity expired.',
      buttonText: 'Go back to seat selection',
      buttonAction: this.onCheckoutCanceled,
    };

    this._modalService.openModal(modalData);
  };

  /**
   * Resets the timer to its initial value.
   */
  public resetTimer = () => {
    clearInterval(this.ref);
    this.ref = undefined;
    this._countdown.next(null);
  };
}
